import { createRoutine } from 'redux-saga-routines'
import { put, call, takeLatest, takeEvery, fork, select, delay } from '@redux-saga/core/effects'
import * as unitsService from 'services/unitsService'
import * as bpService from 'services/BpService'
import { toast } from 'react-toastify'
import { getApiErrors } from 'utils/errors'
import { fetchBpRoutine } from 'features/bpDetails/ducks/actions'
import { selectBpUnits } from 'ducks/units/selectors'
import {
  createBpRoutine
} from 'features/createBp/ducks/actions'
import { fetchAuthUserAccessRequestsRoutine } from 'features/AccessRequest/ducks/actions'
import { notifyFloorPrefill } from 'utils/unitSync'
import { fileUpdateMessage } from 'utils/fileUpdateMessage'

export const fetchUserUnitsRoutine = createRoutine('FETCH_USER_UNITS')
export const fetchBpUnitsRoutine = createRoutine('FETCH_BP_UNITS')
export const fetchBpUnitsForAccessRequestRoutine = createRoutine(
  'FETCH_BP_UNITS_FOR_ACCESS_REQUEST'
)
export const fetchUnitRoutine = createRoutine('FETCH_UNIT')
export const addBpUnitsRoutine = createRoutine('ADD_BP_UNITS')
export const updateBpUnitRoutine = createRoutine('UPDATE_BP_UNIT')
export const updateBpUnitInlineRoutine = createRoutine('UPDATE_BP_UNIT_INLINE')
export const removeBpUnitRoutine = createRoutine('REMOVE_BP_UNIT')
export const removeBpUnitsWithoutUprnRoutine = createRoutine('REMOVE_BP_UNITS_WITHOUT_UPRN')
export const setSelectedUnitRoutine = createRoutine('SET_SELECTED_UNIT')
export const updateSelectedUnitRoutine = createRoutine('UPDATE_SELECTED_UNIT')
export const clearSelectedUnitRoutine = createRoutine('CLEAR_SELECTED_UNIT')
export const editUserRelationToUnitRoutine = createRoutine(
  'EDIT_USER_RELATION_TO_UNIT'
)
export const assignUserToUnitRoutine = createRoutine('ASSIGN_USER_TO_UNIT')
export const inviteUserToUnitRoutine = createRoutine('INVITE_USER_TO_UNIT')
export const fetchUnitInvitationsListRoutine = createRoutine(
  'GET_UNIT_INVITATIONS_LIST'
)
export const revokeAccessToUnitRoutine = createRoutine('REVOKE_ACCESS_TO_UNIT')
export const cancelUnitInvitationRoutine = createRoutine(
  'CANCEL_UNIT_INVITATION'
)
export const reactivateUnitUserRoutine = createRoutine('REACTIVATE_UNIT_USER')
export const detachUnitUserRoutine = createRoutine('DETACH_UNIT_USER')
export const fetchNoticeBoardRoutine = createRoutine('FETCH_NOTICE_BOARD')
export const getFloorPlansForUnitRoutine = createRoutine(
  'GET_FLOORPLANS_FOR_UNIT'
)
export const updateUnitsInCreatedBpRoutine = createRoutine(
  'UPDATE_UNITS_IN_CREATED_BP'
)
export const getUnitInvitationRoutine = createRoutine('GET_UNIT_INVITATION')

export const requestAccessToUnitRoutine = createRoutine(
  'REQUEST_ACCESS_TO_UNIT'
)
export const getUnitAccessRequestsRoutine = createRoutine(
  'GET_UNIT_ACCESS_REQUESTS'
)
export const acceptUnitAccessRequestRoutine = createRoutine(
  'ACCEPT_UNIT_ACCESS_REQUEST'
)
export const denyUnitAccessRequestRoutine = createRoutine(
  'DENY_UNIT_ACCESS_REQUEST'
)

export const syncUnitsRoutine = createRoutine('SYNC_BP_UNITS')
export const previewUnitSyncRoutine = createRoutine('PREVIEW_BP_UNIT_SYNC')
export const updateUnitFileRoutine = createRoutine('UPDATE_UNIT_FILE')
export const removeUnitFileRoutine = createRoutine('REMOVE_UNIT_FILE')

export const getUserRelationToUnitRoutine = createRoutine(
  'GET_USER_RELATION_TO_UNIT'
)

export const unitAccessFetchRequestsRoutine = createRoutine('UNIT_ACCESS/FETCH_REQUESTS')
export const unitAccessFetchInvitationsRoutine = createRoutine('UNIT_ACCESS/FETCH_INVITATIONS')
export const unitAccessFetchRevokedRoutine = createRoutine('UNIT_ACCESS/FETCH_REVOKED')
export const unitAccessRevokeRoutine = createRoutine('UNIT_ACCESS/REVOKE')
export const unitAccessApproveInvitationRoutine = createRoutine('UNIT_ACCESS_APPROVE_INVITATION');
export const unitAccessApproveRequestRoutine = createRoutine('UNIT_ACCESS_APPROVE_REQUEST');
export const unitAccessDenyRoutine = createRoutine('UNIT_ACCESS/DENY')
export const unitAccessReactivateRoutine = createRoutine('UNIT_ACCESS/REACTIVATE');
export const unitAccessDeleteRoutine = createRoutine('UNIT_ACCESS/DELETE');
export const unitAccessFetchUnitRoutine = createRoutine('UNIT_ACCESS/FETCH_UNIT')

function* fetchUserUnits({ payload }) {
  yield put(fetchUserUnitsRoutine.request())
  try {
    const { data } = yield call(unitsService.fetchUserUnits, payload)
    yield put(
      fetchUserUnitsRoutine.success({
        data: data.data || [],
        meta: data.meta || {}
      })
    )
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(fetchUserUnitsRoutine.failure(error))
  }
}

function* fetchNoticeBoard({ payload }) {
  yield put(fetchNoticeBoardRoutine.request())
  try {
    const { data } = yield call(unitsService.fetchNoticeBoard, payload)
    yield put(fetchNoticeBoardRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(fetchNoticeBoardRoutine.failure(error))
  }
}

export function* fetchBpUnits({ payload }) {
  yield put(fetchBpUnitsRoutine.request())
  try {
    const { data } = yield call(unitsService.fetchBpUnits, payload)
    yield put(fetchBpUnitsRoutine.success({ items: data.data, total: data.meta?.total ?? (data.data || []).length, bpId: payload?.bpId }))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(fetchBpUnitsRoutine.failure(error))
  }
}

function* fetchBpUnitsForAccessRequest({ payload }) {
  yield put(fetchBpUnitsForAccessRequestRoutine.request())
  try {
    const { data } = yield call(
      unitsService.fetchBpUnitsForAccessRequest,
      payload
    )
    yield put(fetchBpUnitsForAccessRequestRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(fetchBpUnitsForAccessRequestRoutine.failure(error))
  }
}

function* updateUnitsInCreatedBp({ payload }) {
  yield put(updateUnitsInCreatedBpRoutine.success(payload))
}

function* getFloorPlansForUnit({ payload }) {
  yield put(getFloorPlansForUnitRoutine.request())
  try {
    const { data } = yield call(unitsService.getFloorPlansForUnit, payload)
    yield put(getFloorPlansForUnitRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getFloorPlansForUnitRoutine.failure(error))
  }
}

function* fetchUnit({ payload }) {
  const { editForm } = payload
  yield put(fetchUnitRoutine.request())
  try {
    const { data } = yield call(unitsService.fetchUnit, payload)
    if (editForm) {
      yield put(setSelectedUnitRoutine(data.data))
    } else {
      yield put(fetchUnitRoutine.success(data.data))
    }
  } catch (error) {
    if (error?.response?.status === 404) {
      toast.info('This unit is no longer available. It may have been removed from this Building Passport.')
      yield put(fetchUnitRoutine.failure({ notFound: true }))
    } else {
      getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
      yield put(fetchUnitRoutine.failure(error))
    }
  }
}

function* addBpUnits({ payload }) {
  const { isWizard } = payload
  yield put(addBpUnitsRoutine.request())
  try {
    const { data } = yield call(unitsService.addBpUnits, payload)
    yield put(addBpUnitsRoutine.success(data.data))
    yield put(fetchBpUnitsRoutine({ bpId: payload.bpId }))
    if (!isWizard) {
      const bpResult = yield call(bpService.fetchBp, payload.bpId)
      yield put(fetchBpRoutine.success(bpResult.data.data))
    }
    toast.success(isWizard
      ? 'Unit has been added'
      : 'Units added. They are now enrolled into your all-unit obligations, so your compliance score may dip until each new unit has its evidence.')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(addBpUnitsRoutine.failure(error))
  }
}

export function* syncUnits({ payload }) {
  const { isWizard, callback } = payload
  yield put(syncUnitsRoutine.request())
  try {
    const unitsBefore = yield select(selectBpUnits)
    const countBefore = unitsBefore.length

    const { data } = yield call(unitsService.syncUnits, payload)
    yield put(syncUnitsRoutine.success(data.data))
    const syncMeta = data.meta || {}
    let manualDraftsCreatedCount = 0

    if (isWizard && Array.isArray(syncMeta.conflicts) && syncMeta.conflicts.length > 0) {
      const manualUnits = syncMeta.conflicts.map((conflict, index) => ({
        name: conflict.requestedName || conflict.existingUnitName || conflict.requestedAddress || `Imported unit ${index + 1}`,
        building_type: conflict.requestedBuildingType || 'residential',
        uprn: null,
      }))

      if (manualUnits.length > 0) {
        yield call(unitsService.addManualBpUnits, {
          bpId: payload.bpId,
          units: manualUnits
        })
        manualDraftsCreatedCount = manualUnits.length
      }
    }

    // Fetch updated units list and wait for result
    const unitsResult = yield call(unitsService.fetchBpUnits, { bpId: payload.bpId })
    const unitsAfter = unitsResult?.data?.data || []
    yield put(fetchBpUnitsRoutine.success({ items: unitsAfter, total: unitsResult?.data?.meta?.total ?? unitsAfter.length, bpId: payload.bpId }))

    if (!isWizard) {
      const bpResult = yield call(bpService.fetchBp, payload.bpId)
      yield put(fetchBpRoutine.success(bpResult.data.data))
    }

    const unitsCreated = data.data?.length || 0
    const countAfter = unitsAfter.length
    const unitsRemoved = Math.max(0, countBefore - countAfter + unitsCreated + manualDraftsCreatedCount)

    if (unitsCreated > 0 && unitsRemoved > 0) {
      const skippedElsewhere = Number(syncMeta.skippedExistingElsewhere || 0)
      toast.success(
        manualDraftsCreatedCount > 0
          ? `${unitsCreated} units added, ${manualDraftsCreatedCount} conflicting unit${manualDraftsCreatedCount !== 1 ? 's' : ''} added as manual drafts, ${unitsRemoved} units removed`
          : skippedElsewhere > 0
            ? `${unitsCreated} units added, ${unitsRemoved} units removed, ${skippedElsewhere} skipped because they already exist on another Building Passport`
            : `${unitsCreated} units added, ${unitsRemoved} units removed`
      )
    } else if (unitsCreated > 0) {
      const skippedElsewhere = Number(syncMeta.skippedExistingElsewhere || 0)
      toast.success(
        manualDraftsCreatedCount > 0
          ? `${unitsCreated} units added, ${manualDraftsCreatedCount} conflicting unit${manualDraftsCreatedCount !== 1 ? 's' : ''} added as manual drafts`
          : skippedElsewhere > 0
            ? `${unitsCreated} units added, ${skippedElsewhere} skipped because they already exist on another Building Passport`
            : `${unitsCreated} units added`
      )
    } else if (unitsRemoved > 0) {
      toast.success(`${unitsRemoved} units removed`)
    } else if (manualDraftsCreatedCount > 0) {
      toast.info(
        `${manualDraftsCreatedCount} selected unit${manualDraftsCreatedCount !== 1 ? 's were' : ' was'} added as manual draft${manualDraftsCreatedCount !== 1 ? 's' : ''} because the same UPRN already exists on another Building Passport`
      )
    } else if (Number(syncMeta.skippedExistingElsewhere || 0) > 0) {
      toast.warning(
        `${syncMeta.skippedExistingElsewhere} selected unit${Number(syncMeta.skippedExistingElsewhere) !== 1 ? 's were' : ' was'} not created because the same UPRN already exists on another Building Passport`
      )
    } else if (Number(syncMeta.skippedReferenceAddress || 0) > 0 && countAfter === 0) {
      toast.info('No units were created because the selected reference address is treated as the building itself, not a separate unit.')
    } else {
      toast.success('Units synced')
    }
    notifyFloorPrefill(syncMeta)
    typeof callback === 'function' && callback({
      createdCount: unitsCreated,
      removedCount: unitsRemoved,
      unitsCountAfter: countAfter,
      manualDraftsCreatedCount,
      meta: syncMeta,
      ok: true
    })
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(syncUnitsRoutine.failure(error))
    typeof callback === 'function' && callback({
      createdCount: 0,
      removedCount: 0,
      unitsCountAfter: 0,
      meta: {},
      ok: false
    })
  }
}

function* previewUnitSync({ payload }) {
  const { callback, suppressErrorToast } = payload
  yield put(previewUnitSyncRoutine.request())
  try {
    const { data } = yield call(unitsService.previewUnitSync, payload)
    yield put(previewUnitSyncRoutine.success(data.data))
    typeof callback === 'function' && callback(data.data)
  } catch (error) {
    const message = getApiErrors(error)?.trim()
    message && !suppressErrorToast && toast.error(message)
    yield put(previewUnitSyncRoutine.failure(error))
    typeof callback === 'function' && callback(null, error)
  }
}

function* updateBpUnit({ payload }) {
  const { updateSelected, isCreationFlow, callback } = payload
  yield put(updateBpUnitRoutine.request())
  try {
    const { data } = yield call(unitsService.editBpUnit, payload)
    yield put(updateBpUnitRoutine.success(data.data))
    const bpData = yield call(bpService.fetchBp, payload.bpId)
    if (updateSelected) {
      yield put(updateSelectedUnitRoutine(payload))
    }
    if (isCreationFlow) {
      yield put(createBpRoutine.success(bpData.data.data))
    }
    yield put(fetchBpUnitsRoutine({ bpId: payload.bpId }))
    yield put(fetchBpRoutine({ id: payload.bpId }))
    typeof callback === 'function' && callback()
    toast.success('Unit has been updated')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(updateBpUnitRoutine.failure(error))
  }
}

// Autosave for a single cell of the units table. Sends only the changed
// field, patches the unit in the store in place and skips the list refetch
// and success toast so editing many cells in a row stays quiet and quick.
function* updateBpUnitInline({ payload }) {
  const { bpId, unitId, patch, onSuccess, onError } = payload
  yield put(updateBpUnitInlineRoutine.request({ unitId, patch }))
  try {
    const { data } = yield call(unitsService.patchBpUnit, { bpId, unitId, patch })
    yield put(updateBpUnitInlineRoutine.success({ unitId, unit: data.data, patch }))
    if (Object.prototype.hasOwnProperty.call(patch, 'area')) {
      // Changing a unit's area can move the building type area on the BP.
      yield put(fetchBpRoutine({ id: bpId }))
    }
    typeof onSuccess === 'function' && onSuccess(data.data)
  } catch (error) {
    const message = getApiErrors(error)?.trim() || 'Could not save the change'
    yield put(updateBpUnitInlineRoutine.failure({ unitId, patch, error }))
    typeof onError === 'function' && onError(message, error)
  }
}

function* removeBpUnit({ payload }) {
  const { isWizard } = payload
  yield put(removeBpUnitRoutine.request())
  try {
    yield call(unitsService.removeBpUnit, payload)
    yield put(removeBpUnitRoutine.success())
    yield put(fetchBpUnitsRoutine({ bpId: payload.bpId }))
    if (!isWizard) {
      const { data } = yield call(bpService.fetchBp, payload.bpId)
      yield put(fetchBpRoutine.success(data.data))
    }
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(removeBpUnitRoutine.failure(error))
  }
}

function* removeBpUnitsWithoutUprn({ payload }) {
  yield put(removeBpUnitsWithoutUprnRoutine.request())
  try {
    const { data } = yield call(unitsService.removeBpUnitsWithoutUprn, payload)
    const deletedCount = Number(data?.data?.deleted_count || 0)

    yield put(removeBpUnitsWithoutUprnRoutine.success(data.data))
    yield put(fetchBpUnitsRoutine({ bpId: payload.bpId }))

    const bpResult = yield call(bpService.fetchBp, payload.bpId)
    yield put(fetchBpRoutine.success(bpResult.data.data))

    toast.success(
      deletedCount > 0
        ? `${deletedCount} ${deletedCount === 1 ? 'unit' : 'units'} without UPRN removed`
        : 'No units without UPRN were removed'
    )
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(removeBpUnitsWithoutUprnRoutine.failure(error))
  }
}

function* setSelectedUnit({ payload }) {
  yield put(setSelectedUnitRoutine.request())
  try {
    yield put(setSelectedUnitRoutine.success(payload))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(setSelectedUnitRoutine.failure(error))
  }
}

function* updateSelectedUnit({ payload }) {
  yield put(updateSelectedUnitRoutine.request())
  try {
    yield put(updateSelectedUnitRoutine.success(payload))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(updateSelectedUnitRoutine.failure(error))
  }
}

function* editUserRelationToUnit({ payload }) {
  yield put(editUserRelationToUnitRoutine.request())
  try {
    yield call(unitsService.editUserRelationToUnit, payload)
    yield put(editUserRelationToUnitRoutine.success())
    toast.success('Assignment has been updated')
    yield put(
      fetchUnitRoutine({ bpId: payload.bpId, unitId: payload.unitId })
    )
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(editUserRelationToUnitRoutine.failure(error))
  }
}

function* assignUserToUnit({ payload }) {
  yield put(assignUserToUnitRoutine.request())
  try {
    yield call(unitsService.assignUserToUnit, payload)
    yield put(assignUserToUnitRoutine.success())
    toast.success('User has been assigned')
    yield put(
      fetchUnitRoutine({ bpId: payload.bpId, unitId: payload.unitId })
    )
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(assignUserToUnitRoutine.failure(error))
  }
}

function* inviteUserToUnit({ payload }) {
  yield put(inviteUserToUnitRoutine.request())
  try {
    yield call(unitsService.inviteUserToUnit, payload)
    yield put(inviteUserToUnitRoutine.success())
    toast.success('Invitation has been sent')
    yield put(
      fetchUnitInvitationsListRoutine({
        bpId: payload.bpId,
        unitId: payload.unitId
      })
    )
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(inviteUserToUnitRoutine.failure(error))
  }
}

function* fetchUnitInvitationsList({ payload }) {
  yield put(fetchUnitInvitationsListRoutine.request())
  try {
    const { data } = yield call(unitsService.fetchUnitInvitationsList, payload)
    yield put(fetchUnitInvitationsListRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(fetchUnitInvitationsListRoutine.failure(error))
  }
}

function* revokeAccessToUnit({ payload }) {
  yield put(revokeAccessToUnitRoutine.request())
  try {
    yield call(unitsService.revokeAccessToUnit, payload)
    yield put(revokeAccessToUnitRoutine.success())
    yield put(
      fetchUnitRoutine({ bpId: payload.bpId, unitId: payload.unitId })
    )
    toast.success('Assignment has been revoked')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(revokeAccessToUnitRoutine.failure(error))
  }
}

function* cancelUnitInvitation({ payload }) {
  yield put(cancelUnitInvitationRoutine.request())
  try {
    yield call(unitsService.cancelUnitInvitation, payload)
    yield put(cancelUnitInvitationRoutine.success())
    yield put(
      fetchUnitInvitationsListRoutine({
        bpId: payload.bpId,
        unitId: payload.unitId
      })
    )
    toast.success('Invitation has been deleted')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(cancelUnitInvitationRoutine.failure(error))
  }
}

function* reactivateUnitUser({ payload }) {
  yield put(reactivateUnitUserRoutine.request())
  try {
    yield call(unitsService.reactivateUnitUser, payload)
    yield put(reactivateUnitUserRoutine.success())
    yield put(
      fetchUnitRoutine({ bpId: payload.bpId, unitId: payload.unitId })
    )
    toast.success('User access has been reactivated')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(reactivateUnitUserRoutine.failure(error))
  }
}

function* detachUnitUser({ payload }) {
  yield put(detachUnitUserRoutine.request())
  try {
    yield call(unitsService.detachUnitUser, payload)
    yield put(detachUnitUserRoutine.success())
    yield put(
      fetchUnitRoutine({ bpId: payload.bpId, unitId: payload.unitId })
    )
    toast.success('User has been detached')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(detachUnitUserRoutine.failure(error))
  }
}

function* requestAccessToUnit({ payload }) {
  yield put(requestAccessToUnitRoutine.request())
  try {
    yield call(unitsService.requestAccessToUnit, payload)
    yield put(requestAccessToUnitRoutine.success())
    yield put(fetchAuthUserAccessRequestsRoutine())
    toast.success('Request has been sent')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(requestAccessToUnitRoutine.failure(error))
  }
}

function* getUnitAccessRequests({ payload }) {
  yield put(getUnitAccessRequestsRoutine.request())
  try {
    const { data } = yield call(unitsService.getUnitAccessRequests, payload)
    yield put(getUnitAccessRequestsRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getUnitAccessRequestsRoutine.failure(error))
  }
}

function* acceptUnitAccessRequest({ payload }) {
  yield put(acceptUnitAccessRequestRoutine.request())
  try {
    yield call(unitsService.acceptUnitAccessRequest, payload)
    yield put(getUnitAccessRequestsRoutine(payload))
    yield put(fetchUnitRoutine(payload))
    yield put(acceptUnitAccessRequestRoutine.success())
    toast.success('Request has been accepted')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(acceptUnitAccessRequestRoutine.failure(error))
  }
}

function* denyUnitAccessRequest({ payload }) {
  yield put(denyUnitAccessRequestRoutine.request())
  try {
    yield call(unitsService.denyUnitAccessRequest, payload)
    yield put(getUnitAccessRequestsRoutine(payload))
    yield put(denyUnitAccessRequestRoutine.success())
    toast.success('Request has been denied')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(denyUnitAccessRequestRoutine.failure(error))
  }
}

function* updateUnitFile({ payload }) {
  yield put(updateUnitFileRoutine.request())
  try {
    yield call(unitsService.updateUnitFile, payload)
    yield put(updateUnitFileRoutine.success())
    yield put(
      fetchUnitRoutine({ bpId: payload.bpId, unitId: payload.unitId })
    )
    toast.success(fileUpdateMessage(payload.body, payload.previousName))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(updateUnitFileRoutine.failure(error))
  }
}

function* removeUnitFile({ payload }) {
  yield put(removeUnitFileRoutine.request())
  try {
    yield call(unitsService.removeUnitFile, payload)
    yield put(removeUnitFileRoutine.success())
    yield put(fetchUnitRoutine({ bpId: payload.bpId, unitId: payload.unitId }))
    toast.success('File has been removed')
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(removeUnitFileRoutine.failure(error))
  }
}

function* getUserRelationToUnit({ payload }) {
  yield put(getUserRelationToUnitRoutine.request())
  try {
    const { data } = yield call(unitsService.getUserRelationToUnit, payload)
    yield put(getUserRelationToUnitRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getUserRelationToUnitRoutine.failure(error))
  }
}

function* getUnitInvitation({ payload }) {
  yield put(getUnitInvitationRoutine.request())
  try {
    const { data } = yield call(unitsService.getUnitInvitation, payload)
    yield put(getUnitInvitationRoutine.success(data.data))
  } catch (error) {
    getApiErrors(error)?.trim() && toast.error(getApiErrors(error))
    yield put(getUnitInvitationRoutine.failure(error))
  }
}

function* fetchUnitRequests({ payload }) {
  const { bpId, unitId } = payload
  yield put(unitAccessFetchRequestsRoutine.request())
  try {
    const { data } = yield call(unitsService.getRequests, bpId, unitId)
    yield put(unitAccessFetchRequestsRoutine.success(data.data))
  } catch (error) {
    yield put(unitAccessFetchRequestsRoutine.failure(error))
  }
}

function* fetchUnitInvitations({ payload }) {
  const { bpId, unitId } = payload
  yield put(unitAccessFetchInvitationsRoutine.request())
  try {
    const { data } = yield call(unitsService.getInvitations, bpId, unitId)
    yield put(unitAccessFetchInvitationsRoutine.success(data.data))
  } catch (error) {
    yield put(unitAccessFetchInvitationsRoutine.failure(error))
  }
}

function* fetchUnitRevoked({ payload }) {
  const { bpId, unitId } = payload
  yield put(unitAccessFetchRevokedRoutine.request())
  try {
    const { data } = yield call(unitsService.getRevoked, bpId, unitId)
    yield put(unitAccessFetchRevokedRoutine.success(data.data))
  } catch (error) {
    yield put(unitAccessFetchRevokedRoutine.failure(error))
  }
}

function* approveUnitInvitation({ payload }) {
  const { bpId, unitId, invitationId } = payload;

  yield put(unitAccessApproveInvitationRoutine.request());
  try {
    yield call(unitsService.approveInvitation, { bpId, unitId, invitationId });

    yield put(unitAccessApproveInvitationRoutine.success({ invitationId }));

    yield put(unitAccessFetchInvitationsRoutine.trigger({ bpId, unitId }));
    yield put(unitAccessFetchRequestsRoutine.trigger({ bpId, unitId }));
    yield put(unitAccessFetchUnitRoutine.trigger({ bpId, unitId }));

    toast.success('Invitation approved');
  } catch (error) {
    yield put(unitAccessApproveInvitationRoutine.failure(error));
    toast.error(getApiErrors(error) || 'Could not approve invitation');
  }
}

function* approveUnitRequest({ payload }) {
  const { bpId, unitId, requestId } = payload;

  yield put(unitAccessApproveRequestRoutine.request());
  try {
    yield call(unitsService.approveRequest, { bpId, unitId, requestId });

    yield put(unitAccessApproveRequestRoutine.success({ requestId }));

    yield put(unitAccessFetchRequestsRoutine.trigger({ bpId, unitId }));
    yield put(unitAccessFetchInvitationsRoutine.trigger({ bpId, unitId }));
    yield put(unitAccessFetchUnitRoutine.trigger({ bpId, unitId }));

    toast.success('Access request approved');
  } catch (error) {
    yield put(unitAccessApproveRequestRoutine.failure(error));
    toast.error(getApiErrors(error) || 'Could not approve request');
  }
}

function* denyUnitInvitation({ payload }) {
  const { bpId, unitId, invitationId } = payload;
  yield put(unitAccessDenyRoutine.request());
  try {
    yield call(unitsService.denyInvitation, { bpId, unitId, invitationId });
    yield put(unitAccessDenyRoutine.success());
    toast.success('Invitation denied');

    yield put(unitAccessFetchInvitationsRoutine.trigger({ bpId, unitId }));

  } catch (error) {
    yield put(unitAccessDenyRoutine.failure(error));
    toast.error(getApiErrors(error) || 'Could not deny invitation');
  }
}

function* denyUnitRequest({ payload }) {
  const { bpId, unitId, requestId, reason } = payload;

  yield put(unitAccessDenyRoutine.request());
  try {
    yield call(unitsService.deleteAccessRequest, {
      bpId,
      unitId,
      requestId,
      reason: reason || 'no_reason'
    });

    yield put(unitAccessDenyRoutine.success());
    toast.success('Access request denied');

    yield put(unitAccessFetchRequestsRoutine.trigger({ bpId, unitId }));

  } catch (error) {
    yield put(unitAccessDenyRoutine.failure(error));
    toast.error(getApiErrors(error) || 'Could not deny request');
  }
}

function* revokeUnitAccess({ payload }) {
  const { bpId, unitId, relationId } = payload
  yield put(unitAccessRevokeRoutine.request())
  try {
    yield call(unitsService.revokeAccessToUnit, { bpId, unitId, relationId })

    yield put(unitAccessRevokeRoutine.success())

    yield put(unitAccessFetchRevokedRoutine.trigger({ bpId, unitId }))
    yield put(unitAccessFetchRequestsRoutine.trigger({ bpId, unitId }))
    yield put(unitAccessFetchInvitationsRoutine.trigger({ bpId, unitId }))
    yield put(unitAccessFetchUnitRoutine.trigger({ bpId, unitId }))
    toast.success('Access revoked successfully')
  } catch (error) {
    yield put(unitAccessRevokeRoutine.failure(error))
    toast.error(getApiErrors(error) || 'Could not revoke access')
  }
}

function* reactivateUnitAccess({ payload }) {
  const { bpId, unitId, relationId } = payload;
  yield put(unitAccessReactivateRoutine.request());
  try {
    yield call(unitsService.reactivateAccessToUnit, { bpId, unitId, relationId });
    yield put(unitAccessReactivateRoutine.success());
    yield put(unitAccessFetchRequestsRoutine.trigger({ bpId, unitId }));
    yield put(unitAccessFetchInvitationsRoutine.trigger({ bpId, unitId }));
    yield put(unitAccessFetchRevokedRoutine.trigger({ bpId, unitId }));

    yield put(unitAccessFetchUnitRoutine.trigger({ bpId, unitId }));

    toast.success('Access reactivated');
  } catch (error) {
    yield put(unitAccessReactivateRoutine.failure(error));
    toast.error(getApiErrors(error) || 'Could not reactivate access');
  }
}


function* deleteUnitAccess({ payload }) {
  const { bpId, unitId, relationId } = payload;
  yield put(unitAccessDeleteRoutine.request());
  try {
    yield call(unitsService.deleteAccessToUnit, { bpId, unitId, relationId });
    yield put(unitAccessDeleteRoutine.success());
    yield put(unitAccessFetchRevokedRoutine.trigger({ bpId, unitId }));
    yield put(unitAccessFetchUnitRoutine.trigger({ bpId, unitId }));
    toast.success('Access deleted permanently');
  } catch (error) {
    yield put(unitAccessDeleteRoutine.failure(error));
    toast.error(getApiErrors(error) || 'Could not delete access');
  }
}

function* fetchUnitDetailsWorker({ payload }) {
  const { bpId, unitId } = payload;

  try {
    yield put(unitAccessFetchUnitRoutine.request());
    const { data } = yield call(unitsService.fetchUnitDetails, { bpId, unitId });

    yield put(unitAccessFetchUnitRoutine.success(data.data));
  } catch (error) {
    yield put(unitAccessFetchUnitRoutine.failure(error));
  }
}

function* clearSelectedUnit() {
  yield put(clearSelectedUnitRoutine.success())
}

export function* fetchUserUnitsWatcher() {
  yield takeLatest(fetchUserUnitsRoutine.TRIGGER, fetchUserUnits)
}

export function* fetchBpUnitsWatcher() {
  yield takeLatest(fetchBpUnitsRoutine.TRIGGER, fetchBpUnits)
}

export function* fetchUnitWatcher() {
  yield takeLatest(fetchUnitRoutine.TRIGGER, fetchUnit)
}

export function* addBpUnitsWatcher() {
  yield takeLatest(addBpUnitsRoutine.TRIGGER, addBpUnits)
}

export function* syncUnitsWatcher() {
  yield takeLatest(syncUnitsRoutine.TRIGGER, syncUnits)
}

export function* previewUnitSyncWatcher() {
  yield takeLatest(previewUnitSyncRoutine.TRIGGER, previewUnitSync)
}

export function* updateBpUnitWatcher() {
  yield takeLatest(updateBpUnitRoutine.TRIGGER, updateBpUnit)
}

export function* updateBpUnitInlineWatcher() {
  yield takeEvery(updateBpUnitInlineRoutine.TRIGGER, updateBpUnitInline)
}

export function* removeBpUnitWatcher() {
  yield takeLatest(removeBpUnitRoutine.TRIGGER, removeBpUnit)
}

export function* removeBpUnitsWithoutUprnWatcher() {
  yield takeLatest(removeBpUnitsWithoutUprnRoutine.TRIGGER, removeBpUnitsWithoutUprn)
}

export function* setSelectedUnitWatcher() {
  yield takeLatest(setSelectedUnitRoutine.TRIGGER, setSelectedUnit)
}

export function* clearSelectedUnitWatcher() {
  yield takeLatest(clearSelectedUnitRoutine.TRIGGER, clearSelectedUnit)
}

export function* editUserRelationToUnitWatcher() {
  yield takeLatest(
    editUserRelationToUnitRoutine.TRIGGER,
    editUserRelationToUnit
  )
}

export function* assignUserToUnitWatcher() {
  yield takeLatest(assignUserToUnitRoutine.TRIGGER, assignUserToUnit)
}

export function* inviteUserToUnitWatcher() {
  yield takeLatest(inviteUserToUnitRoutine.TRIGGER, inviteUserToUnit)
}

export function* fetchUnitInvitationsListWatcher() {
  yield takeLatest(
    fetchUnitInvitationsListRoutine.TRIGGER,
    fetchUnitInvitationsList
  )
}

export function* revokeAccessToUnitWatcher() {
  yield takeLatest(revokeAccessToUnitRoutine.TRIGGER, revokeAccessToUnit)
}

export function* cancelUnitInvitationWatcher() {
  yield takeLatest(cancelUnitInvitationRoutine.TRIGGER, cancelUnitInvitation)
}

export function* reactivateUnitUserWatcher() {
  yield takeLatest(reactivateUnitUserRoutine.TRIGGER, reactivateUnitUser)
}

export function* detachUnitUserWatcher() {
  yield takeLatest(detachUnitUserRoutine.TRIGGER, detachUnitUser)
}

export function* updateSelectedUnitWatcher() {
  yield takeLatest(updateSelectedUnitRoutine.TRIGGER, updateSelectedUnit)
}

export function* fetchNoticeBoardWatcher() {
  yield takeLatest(fetchNoticeBoardRoutine.TRIGGER, fetchNoticeBoard)
}

export function* getFloorPlansForUnitWatcher() {
  yield takeLatest(getFloorPlansForUnitRoutine.TRIGGER, getFloorPlansForUnit)
}

export function* updateUnitsInCreatedBpWatcher() {
  yield takeLatest(
    updateUnitsInCreatedBpRoutine.TRIGGER,
    updateUnitsInCreatedBp
  )
}

export function* requestAccessToUnitWatcher() {
  yield takeLatest(requestAccessToUnitRoutine.TRIGGER, requestAccessToUnit)
}

export function* getUnitAccessRequestsWatcher() {
  yield takeLatest(getUnitAccessRequestsRoutine.TRIGGER, getUnitAccessRequests)
}

export function* acceptUnitAccessRequestWatcher() {
  yield takeLatest(
    acceptUnitAccessRequestRoutine.TRIGGER,
    acceptUnitAccessRequest
  )
}

export function* denyUnitAccessRequestWatcher() {
  yield takeLatest(denyUnitAccessRequestRoutine.TRIGGER, denyUnitAccessRequest)
}

export function* updateUnitFileWatcher() {
  yield takeLatest(updateUnitFileRoutine.TRIGGER, updateUnitFile)
}

export function* removeUnitFileWatcher() {
  yield takeLatest(removeUnitFileRoutine.TRIGGER, removeUnitFile)
}

export function* getUserRelationToUnitWatcher() {
  yield takeLatest(getUserRelationToUnitRoutine.TRIGGER, getUserRelationToUnit)
}

export function* getUnitInvitationWatcher() {
  yield takeLatest(getUnitInvitationRoutine.TRIGGER, getUnitInvitation)
}

export function* fetchBpUnitsForAccessRequestWatcher() {
  yield takeLatest(
    fetchBpUnitsForAccessRequestRoutine.TRIGGER,
    fetchBpUnitsForAccessRequest
  )
}

export function* fetchUnitRequestsWatcher() {
  yield takeLatest(unitAccessFetchRequestsRoutine.TRIGGER, fetchUnitRequests)
}

export function* fetchUnitInvitationsWatcher() {
  yield takeLatest(unitAccessFetchInvitationsRoutine.TRIGGER, fetchUnitInvitations)
}

export function* fetchUnitRevokedWatcher() {
  yield takeLatest(unitAccessFetchRevokedRoutine.TRIGGER, fetchUnitRevoked)
}

export function* approveUnitInvitationWatcher() {
  yield takeLatest(unitAccessApproveInvitationRoutine.TRIGGER, approveUnitInvitation);
}

export function* approveUnitRequestWatcher() {
  yield takeLatest(unitAccessApproveRequestRoutine.TRIGGER, approveUnitRequest);
}

export function* revokeUnitAccessWatcher() {
  yield takeLatest(unitAccessRevokeRoutine.TRIGGER, revokeUnitAccess)
}

export function* reactivateUnitAccessWatcher() {
  yield takeLatest(unitAccessReactivateRoutine.TRIGGER, reactivateUnitAccess);
}

export function* deleteUnitAccessWatcher() {
  yield takeLatest(unitAccessDeleteRoutine.TRIGGER, deleteUnitAccess);
}

export function* fetchUnitAccessWatcher() {
  yield takeLatest(unitAccessFetchUnitRoutine.TRIGGER, fetchUnitDetailsWorker)
}
export function* denyUnitWatcher() {
  yield takeLatest(unitAccessDenyRoutine.TRIGGER, function*(action) {
    if (action.payload.type === 'invitation') {
      yield* denyUnitInvitation(action);
    } else {
      yield* denyUnitRequest(action);
    }
  });
}

// SAGAS
export const unitSagas = [
  fork(fetchUserUnitsWatcher),
  fork(fetchBpUnitsWatcher),
  fork(fetchUnitWatcher),
  fork(updateBpUnitWatcher),
  fork(updateBpUnitInlineWatcher),
  fork(removeBpUnitWatcher),
  fork(removeBpUnitsWithoutUprnWatcher),
  fork(addBpUnitsWatcher),
  fork(syncUnitsWatcher),
  fork(previewUnitSyncWatcher),
  fork(setSelectedUnitWatcher),
  fork(clearSelectedUnitWatcher),
  fork(editUserRelationToUnitWatcher),
  fork(assignUserToUnitWatcher),
  fork(inviteUserToUnitWatcher),
  fork(fetchUnitInvitationsListWatcher),
  fork(revokeAccessToUnitWatcher),
  fork(cancelUnitInvitationWatcher),
  fork(reactivateUnitUserWatcher),
  fork(detachUnitUserWatcher),
  fork(updateSelectedUnitWatcher),
  fork(fetchNoticeBoardWatcher),
  fork(getFloorPlansForUnitWatcher),
  fork(updateUnitsInCreatedBpWatcher),
  fork(requestAccessToUnitWatcher),
  fork(getUnitAccessRequestsWatcher),
  fork(acceptUnitAccessRequestWatcher),
  fork(denyUnitAccessRequestWatcher),
  fork(updateUnitFileWatcher),
  fork(removeUnitFileWatcher),
  fork(getUserRelationToUnitWatcher),
  fork(getUnitInvitationWatcher),
  fork(fetchBpUnitsForAccessRequestWatcher),
  fork(fetchUnitRequestsWatcher),
  fork(fetchUnitInvitationsWatcher),
  fork(fetchUnitRevokedWatcher),
  fork(approveUnitInvitationWatcher),
  fork(approveUnitRequestWatcher),
  fork(denyUnitWatcher),
  fork(revokeUnitAccessWatcher),
  fork(reactivateUnitAccessWatcher),
  fork(deleteUnitAccessWatcher),
  fork(fetchUnitAccessWatcher)
]
